import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  Edit3,
  X,
  Mail,
} from 'lucide-react';

interface EditorProfile {
  id: string;
  email: string;
  full_name?: string;
  phone?: string;
  role: string;
  is_active?: boolean;
}

export function EditorManagement() {
  const { profile } = useAuth();
  const { setPageTitle, setHeaderAction } = useAdminLayoutContext();
  const [showModal, setShowModal] = useState(false);
  const [editingEditor, setEditingEditor] = useState<EditorProfile | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
  });

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      fullName: '',
      phone: '',
    });
  };

  // Set page title and header action
  useEffect(() => {
    setPageTitle('Editorer');
    setHeaderAction(
      <button
        onClick={() => {
          resetForm();
          setShowModal(true);
        }}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
      >
        <Plus className="h-5 w-5" />
        Ny editor
      </button>
    );
    return () => {
      setPageTitle(null);
      setHeaderAction(null);
    };
  }, [setPageTitle, setHeaderAction]);

  // Fetch editors only
  const { data: editors, loading: editorsLoading, refetch } = useSupabaseQuery<EditorProfile[]>(
    'profiles',
    {
      select: '*',
      filter: (q: any) => q.eq('role', 'editor'),
      order: { column: 'full_name', ascending: true },
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingEditor) {
        // Update existing editor
        const { error } = await supabase
          .from('profiles')
          .update({
            full_name: formData.fullName,
            phone: formData.phone || null,
            role: 'editor',
          })
          .eq('id', editingEditor.id);

        if (error) throw error;
      } else {
        // Create new editor via RPC
        const { data, error } = await supabase.rpc('create_user_with_profile', {
          p_user_id: crypto.randomUUID(),
          p_email: formData.email,
          p_password: formData.password,
          p_full_name: formData.fullName,
          p_phone: formData.phone || null,
          p_role: 'editor',
          p_department_id: null,
          p_company_id: null,
          p_is_active: true,
        });

        if (error) throw error;
        if (data && data.success === false) {
          throw new Error(data.error || 'Ukjent feil ved opprettelse');
        }
      }

      setShowModal(false);
      setEditingEditor(null);
      resetForm();
      refetch();
    } catch (error: any) {
      console.error('Error saving editor:', error);
      alert(`Feil ved lagring: ${error.message}`);
    }
  };

  const handleEdit = (editor: EditorProfile) => {
    setEditingEditor(editor);
    setFormData({
      email: editor.email,
      password: '',
      fullName: editor.full_name || '',
      phone: editor.phone || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (editor: EditorProfile) => {
    if (!window.confirm(`Er du sikker på at du vil slette ${editor.full_name || editor.email}?`)) {
      return;
    }

    try {
      const { error } = await supabase.from('profiles').delete().eq('id', editor.id);
      if (error) throw error;
      refetch();
    } catch (error: any) {
      console.error('Error deleting editor:', error);
      alert(`Feil ved sletting: ${error.message}`);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingEditor(null);
    resetForm();
  };

  // Only superadmins can access this page
  const isSuperAdmin = profile?.role === 'superadmin';

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-400">Du har ikke tilgang til denne siden.</p>
        </div>
      </div>
    );
  }

  const editorList = editors || [];

  return (
    <div className="space-y-6">
      {/* Editors List */}
      {editorList.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Edit3 className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">Ingen editorer registrert</p>
          <p className="text-gray-500 text-sm">Editorer kan administrere symptomgrupper, tilnærminger, påvirkningsregler og se statistikk</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {editorList.map((editor) => (
            <div
              key={editor.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                    <Edit3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {editor.full_name || 'Ikke satt'}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Mail className="w-4 h-4" />
                      {editor.email}
                    </div>
                    {editor.phone && (
                      <p className="text-sm text-gray-500 mt-1">
                        {editor.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(editor)}
                    className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                    title="Rediger"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(editor)}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Slett"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancel();
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingEditor ? 'Rediger editor' : 'Ny editor'}
              </h2>
              <button
                onClick={handleCancel}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  E-post *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={!!editingEditor}
                />
              </div>

              {/* Password (only for new) */}
              {!editingEditor && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Passord *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fullt navn *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                >
                  {editingEditor ? 'Lagre endringer' : 'Opprett editor'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
